'use client';

import Lottie from 'lottie-react';
import { useRef } from 'react';

import AiiAFooter from '@commons/assets/lottie/aiia-footer.json';
import AiiAPromoBannerDesktop from '@commons/assets/lottie/aiia-promo-banner-desktop.json';
import AiiAPromoBannerMobile from '@commons/assets/lottie/aiia-promo-banner-mobile.json';
import { type BaseContainerProps } from '@commons/containers/BaseContainer';
import { useMediaQueries } from '@commons/contexts/responsive';

import { LOTTIE_ANIMATION } from './animations';
import styles from './index.module.scss';

// Type definition for the LottiePlayer component properties.

type Props = BaseContainerProps & {
  animation: LOTTIE_ANIMATION;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
};

/**
 * Decorative Lottie background animation player component.
 *
 * Restrict the Lottie animation to a predefined set using a factory map,
 *
 * @see https://github.com/Gamote/lottie-react
 */

const LottiePlayer = ({ animation, loop = true, autoplay = true, className }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { ready, minWidth } = useMediaQueries();

  // Lottie files should not be loaded until we know the screen size.
  if (!ready) return null;

  let data;
  const isMobile = !minWidth['md'];

  switch (animation) {
    case LOTTIE_ANIMATION.AiiA_PromoBanner:
      data = isMobile ? AiiAPromoBannerMobile : AiiAPromoBannerDesktop;
      break;
    case LOTTIE_ANIMATION.AiiA_Footer:
      data = AiiAFooter;
      break;
  }

  const handleDOMLoaded = () => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;

    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('role', 'presentation');
  };

  return (
    <div ref={containerRef} className={styles.container} aria-hidden="true">
      <Lottie
        animationData={data}
        loop={loop}
        autoplay={autoplay}
        className={className}
        rendererSettings={{ focusable: false }}
        onDOMLoaded={handleDOMLoaded}
      />
    </div>
  );
};

export default LottiePlayer;
